import ChatWindowHeader from '../chat-window-header/ChatWindowHeader';
import useSessionEvents from '../hooks/useSessionEvents';
import useLoadChatMessages from '../hooks/useLoadChatMessages';
import useRefLive from '../../hooks/useRefLive';
import Loader from '../../ui/loader/Loader';
import Alert from '../../ui/alert/Alert';
// import Message from '../../features/chat/elements/ui/message/Message';
import { ChatViewLayoutProps } from '../../layouts/chat-view-layout/ChatViewLayout';
import { Data } from '../../ui/markdown-wrapper/hooks/useActions/actions/data/Data';
import { ObjectTypes } from '../../ui/markdown-wrapper/hooks/useActions/useActions';
import StreamingViewer from './StreamingViewer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SessionEventTypes, SessionEventTypesPrefixes } from '../../models/BackendEvents';
import { sortBy } from '../../helpers/listsHelpers';
import { useLayout } from '../../contexts/layout/LayoutContext';
import style from './WorkflowWindow.module.scss';
import { useChat } from '../../contexts/chat/ChatContext';
import ProductivityGainModal from '../productivity-gain-modal/ProductivityGainModal';
import Button, { ButtonThemes } from '../../ui/button/Button';
import { useNavigate } from 'react-router-dom';
import useInfo from '../../hooks/useInfo';
import useLabels from '../../hooks/useLabels';


const WorkflowWindow = ({ onOpenSidebar, chatId, onReloadChats }: ChatViewLayoutProps) => {
    const { isRightSidebarVisible, isLeftSidebarVisible, hideLeftSidebar, collapseLeftSidebar, expandLeftSidebar } = useLayout();
    const { events, setEvents } = useSessionEvents({ session_id: chatId ?? "" });
    const { chat, isLoadingChatHistory, chatHistoryError, renderChatHistoryError, reload } = useLoadChatMessages({ chatId });
    const lastWorkflowEvent = useMemo(() => sortBy(events, "timestamp", true).find(x => x.type.startsWith(SessionEventTypesPrefixes.workflow)), [events]);
    const labels = useLabels();
    const navigate = useNavigate();
    const { getPersonaFromKey } = useInfo();
    useEffect(() => {
        setEvents([]);
    }, [chatId, setEvents]);

    useEffect(() => {
        setEvents(chat?.events ?? []);
    }, [chat, setEvents]);


    const reloadIfNeeded = useCallback((event: SessionEventTypes, prevEvent: SessionEventTypes) => {
        if (!prevEvent)
            return;

        const type = event;

        if (!type)
            return;
        if ([SessionEventTypes.workflowFailed, SessionEventTypes.workflowCompleted].includes(type)) {
            reload(undefined, true, true);
        }
    }, [reload]);

    useRefLive(lastWorkflowEvent?.type as SessionEventTypes, reloadIfNeeded);


    const content = useMemo(() => {
        if (isLoadingChatHistory || !lastWorkflowEvent)
            return <div><Loader isVisible /></div>;

        if (!!chatHistoryError)
            return renderChatHistoryError;

        switch (lastWorkflowEvent?.type) {
            case SessionEventTypes.workflowFailed:
                return <Alert type='error' headline='Error' description={lastWorkflowEvent.metadata?.error} />;

            case SessionEventTypes.workflowCompleted:
                return <>
                    <Data
                        objectId={lastWorkflowEvent.metadata.artifact ?? 'data'}
                        objectType={ObjectTypes.data}
                        chatId={chat?.session.session_id}
                    />
                    {/* {
                        chat?.messages.map(m => <Message
                            key={m.id}
                            persona={chat?.session.persona}
                            chatId={chat?.session.session_id}
                            feedback={chat.feedback?.find((f) => f.message_id === m.id)}
                            message={m} />)
                    }
                    <br />
                    <br />
                    <br /> */}
                </>;

            case SessionEventTypes.workflowStreaming:
                const streamingMetadata = lastWorkflowEvent.metadata;
                return <StreamingViewer
                    type={streamingMetadata?.streaming_type || 'unknown'}
                    chatId={chat?.session.session_id ?? ''}
                    events={events}
                    onComplete={() => {
                        //console.log"Streaming complete");
                    }}
                />;

            case SessionEventTypes.workflowUpdated:
                const state = lastWorkflowEvent.metadata?.state ?? "Processing";
                return <Alert type='info' headline='Please wait' description={state + ".."} />;
        }
    }, [events, lastWorkflowEvent, chat?.session.session_id, isLoadingChatHistory, chatHistoryError, renderChatHistoryError]); //chat?.messages, chat?.feedback, chat?.session.persona, 

    const { submitProductivityGain } = useChat();
    const [showProductivityModal, setShowProductivityModal] = useState(false);
    const onCloseProductivityModal = useCallback(() => setShowProductivityModal(false), []);
    const onOpenProductivityModal = useCallback(() => setShowProductivityModal(true), []);
    const onSubmitProductivityGain = useCallback(async (productivityGain: number) => {
        if (!chatId) return;
        await submitProductivityGain(chatId, productivityGain);
        setShowProductivityModal(false);
        await onReloadChats?.(true);
    }, [chatId, onReloadChats, submitProductivityGain]);
    const onNavigateToNewChat = useCallback(() => {
        navigate("/" + (getPersonaFromKey(chat?.session.persona ?? "")?.route ?? ""));
    }, [navigate, getPersonaFromKey, chat]);
    return (
        <div className={style['workflow']}>
            <div className={style['wrapper']}>
                <ChatWindowHeader
                    isRightSidebarVisible={isRightSidebarVisible}
                    isLeftSidebarVisible={isLeftSidebarVisible}
                    expandLeftSidebar={() => {
                        expandLeftSidebar();
                        // showLeftSidebar();
                    }}
                    onCreateNewChat={onNavigateToNewChat}
                    collapseLeftSidebar={() => {
                        collapseLeftSidebar();
                        hideLeftSidebar();
                    }}
                    onClockClick={onOpenProductivityModal}
                    initialTitle={chat?.session?.subject || ''}
                    onBurgerClick={onOpenSidebar}
                />
                <div className={style['content']}>
                    {content}
                    {chat?.session.persona && <div style={{ display: 'flex', justifyContent: 'end', borderTop: '1px solid #eee', paddingTop: '1rem', marginTop: '1rem' }}>
                        <Button theme={ButtonThemes.secondary} onClick={onNavigateToNewChat}>{labels.newSession}</Button>
                    </div>
                    }
                </div>
            </div>
            <ProductivityGainModal initialProductivityGain={chat?.session.productivity_gain} chatId={chatId} isOpen={showProductivityModal} onClose={onCloseProductivityModal} onSubmit={onSubmitProductivityGain} />
        </div >
    );
};

export default WorkflowWindow;